import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import UniversalNavbar from '../components/UniversalNavbar';
import VFooter from '../components/VFooter';
import ScrollAnimate, { fadeInUp, fadeInLeft, fadeInRight, scaleUp, staggerContainer } from '../components/ScrollAnimate';

// Reusable Card Component for Benefits and Features
const InfoCard = ({ icon, title, desc, animationVariants = fadeInUp }) => (
  <ScrollAnimate
    variants={animationVariants}
    className="p-8 md:p-10 bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col" // Added flex for consistency
  >
    <div className="w-14 h-14 md:w-16 md:h-16 bg-primary-light rounded-xl flex items-center justify-center mb-6 text-2xl text-primary">
      {icon}
    </div>
    <h3 className="text-xl md:text-2xl text-dark font-semibold mb-4">{title}</h3> {/* Adjusted font weight */}
    <p className="text-muted leading-relaxed text-sm md:text-base">{desc}</p> {/* Adjusted text size */}
  </ScrollAnimate>
);

// Section Title Component
const SectionTitle = ({ children, subtitle, className }) => (
  <ScrollAnimate variants={fadeInUp} className={`text-center mb-12 md:mb-20 ${className}`}>
    <h2 className="text-3xl sm:text-4xl md:text-5xl text-dark font-bold mb-3 md:mb-4">{children}</h2>
    {subtitle && <p className="text-lg md:text-xl text-muted max-w-xl mx-auto">{subtitle}</p>}
  </ScrollAnimate>
);


const LandingPage = () => {
  const heroTextContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2, delayChildren: 0.3 } }
  };

  const heroItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const benefits = [
    { icon: "🎨", title: "Creative Freedom", desc: "Showcase your designs and creativity with customizable profiles tailored to your unique style." },
    { icon: "💼", title: "Expand Your Reach", desc: "Connect with a diverse audience of fashion enthusiasts seeking personalized and unique designs." },
    { icon: "💸", title: "Secure Earnings", desc: "Earn with transparent payment processing and instant settlements." },
    { icon: "🌐", title: "Hassle-Free Platform", desc: "Easily showcase and sell your designs with user-friendly tools, seamless management features, and support tailored to your needs." },
    { icon: "🤝", title: "Seamless Collaboration", desc: "Let customers share their dream outfits, and quote your price to make their vision a reality." },
    { icon: "🔒", title: "Data Security", desc: "Your data and designs are protected with industry-standard security measures." }
  ];

  const processSteps = [
    { step: "1", title: "Sign Up & Profile Setup", desc: "Create a profile in minutes with your portfolio, brand details, and design philosophy." },
    { step: "2", title: "Upload Your Designs", desc: "Showcase collections with high-quality images, pricing, and customization options." },
    { step: "3", title: "Receive Orders", desc: "Customers place orders or submit customization requests directly to you." },
    { step: "4", title: "Deliver and Earn", desc: "Fulfill orders and enjoy secure, hassle-free payments." }
  ];

  const features = [
    { icon: "📱", title: "Exposure", desc: "Get discovered by customers across India and scale your design business effortlessly." },
    { icon: "📦", title: "Order Management", desc: "Track orders with tools, ensuring smooth communication and timely updates for your customers." },
    { icon: "🔍", title: "Customization Requests", desc: "Offer bespoke design options and collaborate directly with customers to bring their ideas to life." }
  ];

  return (
    <div className="bg-white overflow-x-hidden font-fell flex flex-col min-h-screen">
      <UniversalNavbar />

      {/* Enhanced Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-gradient-to-br from-primary-light via-primary-light to-white relative overflow-hidden">
        {/* Enhanced Decorative Elements */}
        <motion.div
          className="absolute top-[-20%] right-[-30%] w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] lg:w-[500px] lg:h-[500px] bg-gradient-to-br from-primary/10 to-primary/5 rounded-full z-0"
          initial={{ scale: 0.8, opacity: 0, rotate: -45 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 2, ease: "circOut", delay: 0.3 }}
        />
        <motion.div
          className="absolute bottom-[-10%] left-[-20%] w-[200px] h-[200px] sm:w-[250px] sm:h-[250px] bg-gradient-to-tr from-primary/5 to-transparent rounded-full z-0"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: "circOut", delay: 0.8 }}
        />

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 xl:gap-20 items-center relative z-10">
          {/* Text Content */}
          <motion.div
            className="text-center lg:text-left order-2 lg:order-1"
            variants={heroTextContainerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              variants={heroItemVariants}
              className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-primary font-semibold text-sm mb-6 shadow-sm border border-primary/20"
            >
              <span className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></span>
              Reach a World of Fashion Enthusiasts
            </motion.div>

            <motion.h1
              variants={heroItemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-dark mb-6 leading-tight"
            >
              Launch Your{' '}
              <span className="text-primary bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
                Design Portfolio
              </span>
            </motion.h1>

            <motion.p
              variants={heroItemVariants}
              className="text-lg sm:text-xl lg:text-2xl text-muted mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0"
            >
              Showcase and sell your custom designs to customers across India with ease. Start your creative journey with{' '}
              <span className="font-semibold text-primary">VASSTR</span>!
            </motion.p>

            <motion.div variants={heroItemVariants} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/register"
                className="group inline-flex items-center justify-center px-8 py-4 bg-primary text-white rounded-xl font-semibold text-lg shadow-lg hover:bg-primary-dark hover:-translate-y-1 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
              >
                <span className="relative z-10">Register Now.</span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={false}
                />
              </Link>
              {/* <Link
                to="#benefits"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary text-primary rounded-xl font-semibold text-lg hover:bg-primary hover:text-white transition-all duration-300"
              >
                Learn More
              </Link> */}
            </motion.div>

            {/* Key Benefits */}
            <motion.div
              variants={heroItemVariants}
              className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center lg:text-left"
            >
              <div className="flex flex-col items-center lg:items-start">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                  <span className="text-primary text-xl">🎨</span>
                </div>
                <div className="text-lg font-semibold text-dark">Creative Freedom</div>
                <div className="text-sm text-muted">Express your unique style</div>
              </div>
              <div className="flex flex-col items-center lg:items-start">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                  <span className="text-primary text-xl">💰</span>
                </div>
                <div className="text-lg font-semibold text-dark">Earn More</div>
                <div className="text-sm text-muted">Higher margins & profits</div>
              </div>
              <div className="flex flex-col items-center lg:items-start">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                  <span className="text-primary text-xl">🌟</span>
                </div>
                <div className="text-lg font-semibold text-dark">Build Brand</div>
                <div className="text-sm text-muted">Grow your reputation</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Image */}
          <motion.div
            className="order-1 lg:order-2 relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent rounded-2xl transform rotate-6"></div>
              <img
                src={`https://d3qvi06vol9hrb.cloudfront.net/static/media/Designer_Image.5add1e148467185f6c89.jpeg`}
                alt="Designer Dashboard"
                className="relative rounded-2xl shadow-2xl w-full h-auto object-cover transform hover:scale-105 transition-transform duration-700"
              />
              {/* Floating Elements */}
              <motion.div
                className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="text-primary font-semibold text-sm">Your Designs</div>
                <div className="text-lg font-bold text-dark">✨ Showcase</div>
              </motion.div>

              <motion.div
                className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              >
                <div className="text-primary font-semibold text-sm">Start Earning</div>
                <div className="text-lg font-bold text-dark">🚀 Today</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-white">
        <SectionTitle subtitle="Empowering Designers to Showcase and Grow Their Brand">
          Why Choose VASSTR?
        </SectionTitle>
        
        <ScrollAnimate
          tag="div" // Specify the tag for the motion component
          variants={staggerContainer(0.15)}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
        >
          {benefits.map((benefit, index) => (
            <InfoCard key={index} {...benefit} animationVariants={fadeInUp} />
          ))}
        </ScrollAnimate>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-primary-light">
        <SectionTitle subtitle="Get Started in a Few Simple Steps">
          How It Works
        </SectionTitle>
        
        <div className="mt-12 md:mt-16 relative">
          {/* Vertical line for all screens, simplified for responsiveness */}
          <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-1 h-full bg-primary opacity-30 rounded-full"></div>
          
          {processSteps.map((item, index) => (
            <ScrollAnimate
              key={index}
              variants={index % 2 === 0 ? fadeInLeft : fadeInRight} // Alternating slide-in for larger screens
              className={`mb-10 md:mb-16 flex group`} // Added group for potential hover effects on dot
            >
              {/* Mobile Layout: Stacked */}
              <div className="md:hidden w-full flex items-start">
                <div className="mr-4">
                  <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg shadow-md">
                    {item.step}
                  </div>
                </div>
                <div className="p-6 bg-white rounded-xl shadow-md flex-1">
                  <h3 className="text-xl font-semibold text-primary mb-2">{item.title}</h3>
                  <p className="text-muted leading-relaxed text-sm">{item.desc}</p>
                </div>
              </div>

              {/* Desktop Layout: Alternating Timeline */}
              <div className={`hidden md:flex w-full items-center ${index % 2 !== 0 ? 'justify-start' : 'justify-end'}`}>
                <div className={`w-full md:w-[45%] p-6 lg:p-8 bg-white rounded-xl shadow-lg relative 
                                ${index % 2 === 0 ? 'md:mr-[10%]' : 'md:ml-[10%] order-last'}`}>
                  <div className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-primary rounded-full border-4 border-primary-light shadow-xl
                                  ${index % 2 === 0 ? 'md:-right-2.5 md:translate-x-1/2' : 'md:-left-2.5 md:-translate-x-1/2'}`}>
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="text-xl lg:text-2xl font-semibold text-primary mb-3">{item.title}</h3>
                  <p className="text-muted leading-relaxed text-sm lg:text-base">{item.desc}</p>
                </div>
              </div>
            </ScrollAnimate>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-white">
        <SectionTitle subtitle="Tools to Simplify and Enhance Your Design Business">
          Powerful Features
        </SectionTitle>
        
        <ScrollAnimate
          tag="div"
          variants={staggerContainer(0.15)}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 mt-12 md:mt-16"
        >
          {features.map((feature, index) => (
            <InfoCard key={index} {...feature} animationVariants={fadeInUp} />
          ))}
        </ScrollAnimate>
      </section>

      <VFooter />
    </div>
  );
};

export default LandingPage;